import { useEffect, useRef, useState } from "react";
import { X, Download, ExternalLink, Loader2 } from "lucide-react";
import { fetchPdfBlobUrl, downloadPdf } from "../lib/api";
import { toast } from "sonner";

// Modale de prévisualisation PDF inline (iframe).
// Props :
//   - open (bool)
//   - onClose (fn)
//   - title (string) — titre affiché en en-tête
//   - path (string) — chemin de l'API renvoyant application/pdf (authentifié)
//   - filename (string) — nom pour le téléchargement
export default function PdfViewerModal({ open, onClose, title, path, filename = "document.pdf" }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentUrl = useRef(null);

  useEffect(() => {
    if (!open || !path) return;
    let alive = true;
    setLoading(true); setError(null); setUrl(null);
    (async () => {
      try {
        const blobUrl = await fetchPdfBlobUrl(path);
        if (!alive) { window.URL.revokeObjectURL(blobUrl); return; }
        currentUrl.current = blobUrl;
        setUrl(blobUrl);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Impossible de charger le PDF.");
      } finally { if (alive) setLoading(false); }
    })();
    return () => {
      alive = false;
      if (currentUrl.current) { window.URL.revokeObjectURL(currentUrl.current); currentUrl.current = null; }
    };
  }, [open, path]);

  const doDownload = async () => {
    try { await downloadPdf(path, filename); }
    catch { toast.error("Téléchargement impossible."); }
  };

  if (!open) return null;
  return (
    <div data-testid="pdf-viewer-modal" className="fixed inset-0 z-[4000] bg-black/60 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="shrink-0 px-4 py-3 border-b border-[#E2DFD8] flex items-center justify-between gap-3 bg-[#FDF8F0]">
          <h3 className="font-head font-black text-sm sm:text-base text-[#1A1A1A] truncate">{title || "Document"}</h3>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="pdf-viewer-download" onClick={doDownload}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold">
              <Download className="w-3.5 h-3.5" /> Télécharger
            </button>
            {url && (
              <a data-testid="pdf-viewer-newtab" href={url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-xs font-bold text-[#1A1A1A]">
                <ExternalLink className="w-3.5 h-3.5" /> Nouvel onglet
              </a>
            )}
            <button data-testid="pdf-viewer-close" onClick={onClose}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-[#75807A] hover:text-[#1A1A1A]">
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 min-h-0 bg-[#E2DFD8]/40 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#F26419]" />
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
              <div>
                <div className="font-bold text-[#D32F2F] mb-1">Erreur de chargement</div>
                <p className="text-xs text-[#75807A]">{error}</p>
              </div>
            </div>
          )}
          {url && !loading && !error && (
            <object
              data-testid="pdf-viewer-object"
              data={url + "#view=FitH&toolbar=1"}
              type="application/pdf"
              className="absolute inset-0 w-full h-full"
            >
              <iframe
                data-testid="pdf-viewer-iframe"
                src={url + "#view=FitH&toolbar=1"}
                title={title}
                className="absolute inset-0 w-full h-full border-0"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-white">
                <p className="text-sm text-[#75807A]">Votre navigateur ne peut pas afficher ce PDF directement.</p>
                <a href={url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#F26419] hover:bg-[#D94D0E] text-white text-sm font-bold">
                  <ExternalLink className="w-4 h-4" /> Ouvrir dans un nouvel onglet
                </a>
              </div>
            </object>
          )}
        </div>
      </div>
    </div>
  );
}
